"use strict";
// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
Object.defineProperty(exports, "__esModule", { value: true });
exports.provisionUtils = void 0;
const arm_resources_1 = require("@azure/arm-resources");
const teamsfx_api_1 = require("@microsoft/teamsfx-api");
const constants_1 = require("../common/constants");
const localizeUtils_1 = require("../common/localizeUtils");
const telemetry_1 = require("../common/telemetry");
const stringUtils_1 = require("../common/stringUtils");
const globalVars_1 = require("../common/globalVars");
const azure_1 = require("../error/azure");
const common_1 = require("../error/common");
const m365_1 = require("../error/m365");
const constants_2 = require("./constants");
const ResourceGroupHelper_1 = require("./utils/ResourceGroupHelper");
const accountUtils_1 = require("../common/accountUtils");
class ProvisionUtils {
    /**
     * make sure subscription is correct before provision for V3
     * subscriptionId is provided from .env.xxx file
     */
    async ensureSubscription(azureAccountProvider, givenSubscriptionId) {
        globalVars_1.TOOLS.logProvider.info("check whether azure account is signed in.");
        // make sure the user is logged in
        await azureAccountProvider.getIdentityCredentialAsync(true);
        if (!givenSubscriptionId) {
            globalVars_1.TOOLS.logProvider.info("subscription is not selected, try to select.");
            try {
                const subscriptionInAccount = await azureAccountProvider.getSelectedSubscription(true);
                if (!subscriptionInAccount) {
                    // this case will not happen actually
                    return (0, teamsfx_api_1.err)(new azure_1.SelectSubscriptionError());
                }
                else {
                    globalVars_1.TOOLS.logProvider.info(`successful to select subscription: ${subscriptionInAccount.subscriptionId}`);
                    return (0, teamsfx_api_1.ok)(subscriptionInAccount);
                }
            }
            catch (e) {
                return (0, teamsfx_api_1.err)((0, common_1.assembleError)(e));
            }
        }
        // verify valid subscription (permission)
        globalVars_1.TOOLS.logProvider.info("subscription is given, try to validate");
        const subscriptions = await azureAccountProvider.listSubscriptions();
        const foundSubscriptionInfo = findSubscriptionFromList(givenSubscriptionId, subscriptions);
        if (!foundSubscriptionInfo) {
            globalVars_1.TOOLS.logProvider.info("subscription validate fail");
            return (0, teamsfx_api_1.err)(new azure_1.InvalidAzureSubscriptionError(givenSubscriptionId));
        }
        globalVars_1.TOOLS.logProvider.info("subscription validate success");
        return (0, teamsfx_api_1.ok)(foundSubscriptionInfo);
    }
    async ensureResourceGroup(inputs, azureAccountProvider, subscriptionId, givenResourceGroupName, defaultResourceGroupName) {
        const azureToken = await azureAccountProvider.getIdentityCredentialAsync();
        if (azureToken === undefined) {
            return (0, teamsfx_api_1.err)(new azure_1.InvalidAzureCredentialError());
        }
        await azureAccountProvider.setSubscription(subscriptionId);
        const rmClient = new arm_resources_1.ResourceManagementClient(azureToken, subscriptionId);
        let resourceGroupInfo;
        if (givenResourceGroupName) {
            const getResourceGroupRes = await ResourceGroupHelper_1.resourceGroupHelper.getResourceGroupInfo(givenResourceGroupName, rmClient);
            if (getResourceGroupRes.isErr()) {
                return (0, teamsfx_api_1.err)(getResourceGroupRes.error);
            }
            else {
                if (!getResourceGroupRes.value) {
                    return (0, teamsfx_api_1.err)(new azure_1.ResourceGroupNotExistError(givenResourceGroupName, subscriptionId));
                }
                else {
                    resourceGroupInfo = getResourceGroupRes.value;
                }
            }
        }
        else {
            const defaultRG = defaultResourceGroupName || "teams-app-rg";
            const rgRes = await ResourceGroupHelper_1.resourceGroupHelper.askResourceGroupInfoV3(inputs, azureAccountProvider, rmClient, defaultRG);
            if (rgRes.isErr())
                return (0, teamsfx_api_1.err)(rgRes.error);
            resourceGroupInfo = rgRes.value;
        }
        return (0, teamsfx_api_1.ok)(resourceGroupInfo);
    }
    async getM365TenantId(m365TokenProvider) {
        // Just to trigger M365 login before the concurrent execution of localDebug.
        // Because concurrent execution of localDebug may getAccessToken() concurrently, which
        // causes 2 M365 logins before the token caching in common lib takes effect.
        const appStudioTokenRes = await m365TokenProvider.getAccessToken({
            scopes: (0, accountUtils_1.isSovereignHigh)() ? constants_1.GraphScopes : (0, constants_1.AppStudioScopes)(),
        });
        if (appStudioTokenRes.isErr()) {
            return (0, teamsfx_api_1.err)(appStudioTokenRes.error);
        }
        const appStudioTokenJsonRes = await m365TokenProvider.getJsonObject({
            scopes: (0, accountUtils_1.isSovereignHigh)() ? constants_1.GraphScopes : (0, constants_1.AppStudioScopes)(),
        });
        const appStudioTokenJson = appStudioTokenJsonRes.isOk()
            ? appStudioTokenJsonRes.value
            : undefined;
        if (appStudioTokenJson === undefined) {
            return (0, teamsfx_api_1.err)(new m365_1.M365TokenJSONNotFoundError());
        }
        const tenantIdInToken = appStudioTokenJson.tid;
        const tenantUserName = appStudioTokenJson.upn ?? appStudioTokenJson.unique_name;
        if (!tenantIdInToken || !(typeof tenantIdInToken === "string")) {
            return (0, teamsfx_api_1.err)(new m365_1.M365TenantIdNotFoundInTokenError());
        }
        return (0, teamsfx_api_1.ok)({ tenantIdInToken, tenantUserName });
    }
    async askForProvisionConsentV3(ctx, m365tenant, azureSubInfo, envName) {
        const azureTokenJson = await ctx.azureAccountProvider.getJsonObject();
        const username = azureTokenJson.unique_name || "";
        const azureAccountInfo = (0, localizeUtils_1.getLocalizedString)("core.provision.azureAccount", username);
        const azureSubscriptionInfo = (0, localizeUtils_1.getLocalizedString)("core.provision.azureSubscription", azureSubInfo.subscriptionName);
        const accountsInfo = [azureAccountInfo, azureSubscriptionInfo];
        if (m365tenant) {
            const m365AccountInfo = (0, localizeUtils_1.getLocalizedString)("core.provision.m365Account", m365tenant?.tenantUserName);
            accountsInfo.push(m365AccountInfo);
        }
        const confirmMsg = (0, localizeUtils_1.getLocalizedString)("core.provision.confirmEnvAndCostNotice", envName);
        const provisionText = (0, localizeUtils_1.getLocalizedString)("core.provision.provision");
        const confirmRes = await ctx.ui?.showMessage("warn", accountsInfo.join("\n") + "\n\n" + confirmMsg, true, provisionText);
        const confirm = confirmRes?.isOk() ? confirmRes.value : undefined;
        ctx.telemetryReporter?.sendTelemetryEvent(telemetry_1.TelemetryEvent.ConfirmProvision, envName
            ? {
                [telemetry_1.TelemetryProperty.Env]: (0, stringUtils_1.getHashedEnv)(envName),
                [constants_2.SolutionTelemetryProperty.SubscriptionId]: azureSubInfo.subscriptionId,
                [constants_2.SolutionTelemetryProperty.M365TenantId]: m365tenant?.tenantIdInToken ?? "",
                [constants_2.SolutionTelemetryProperty.ConfirmRes]: !confirm ? "Cancel" : "Provision",
            }
            : {});
        if (confirm !== provisionText) {
            return (0, teamsfx_api_1.err)(new teamsfx_api_1.UserError("coordinator", "CancelProvision", "CancelProvision"));
        }
        return (0, teamsfx_api_1.ok)(undefined);
    }
    ensureM365TenantMatchesV3(actions, tenantId) {
        if (actions.length === 0 || !tenantId) {
            return (0, teamsfx_api_1.ok)(undefined);
        }
        const hasSwitched = !!process.env.TEAMS_APP_TENANT_ID && process.env.TEAMS_APP_TENANT_ID !== tenantId;
        const keysNeedToUpdate = ["TEAMS_APP_TENANT_ID"];
        if (actions.includes("aadApp/create")) {
            if (process.env.AAD_APP_CLIENT_ID) {
                keysNeedToUpdate.push("AAD_APP_CLIENT_ID");
            }
        }
        if (actions.includes("botAadApp/create") || actions.includes("botFramework/create")) {
            if (process.env.BOT_ID) {
                keysNeedToUpdate.push("BOT_ID");
            }
        }
        const error = new m365_1.M365TenantIdNotMatchError(tenantId, process.env.TEAMS_APP_TENANT_ID, keysNeedToUpdate.join(", "));
        error.helpLink = constants_1.HelpLinks.SwitchTenant;
        return !hasSwitched ? (0, teamsfx_api_1.ok)(undefined) : (0, teamsfx_api_1.err)(error);
    }
}
function findSubscriptionFromList(subscriptionId, subscriptions) {
    return subscriptions.find((item) => item.subscriptionId === subscriptionId);
}
exports.provisionUtils = new ProvisionUtils();
//# sourceMappingURL=provisionUtils.js.map